import { MatchOperator, PatternHelp } from "./MatchOperator";
import { useEffect, useRef, useState, useMemo } from "react";
import {
  presetTarget,
  presetWritable,
  uid,
  type Field,
  type Project,
} from "@proof/core";
import { FieldInput } from "@proof/ui";
import { useAuthoringServices } from "./services";
import { Button, Control } from "./components";

export function PresetRecords({
  field,
  fields,
  onChange,
  project,
  partId,
}: {
  field: Field;
  fields: Field[];
  onChange: (patch: Partial<Field>) => void;
  project?: Project;
  partId?: string;
}) {
  const services = useAuthoringServices(),
    [selected, select] = useState(""),
    [target, chooseTarget] = useState("");
  const dam = useMemo(
    () => (project ? services.dam?.(project, partId) : undefined),
    [services.dam, project, partId],
  );
  const latest = useRef({ field, onChange }),
    live = useRef(true);
  latest.current = { field, onChange };
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const record =
    field.presets.find((r) => r.id === selected) || field.presets[0];
  const modify = (
    id: string,
    update: (record: Field["presets"][number]) => Field["presets"][number],
  ) => {
    if (!live.current || !latest.current.field.presets.some((r) => r.id === id))
      return;
    latest.current.onChange({
      presets: latest.current.field.presets.map((r) =>
        r.id === id ? update(r) : r,
      ),
    });
  };
  const available = fields.filter(
    (f) =>
      f.id !== field.id &&
      presetWritable(f) &&
      !Object.keys(record?.values || {}).some(
        (key) => presetTarget(fields, key).id === f.id,
      ),
  );
  return (
    <div className="preset-records">
      <Control label="Preset option">
        <select
          value={record?.id || ""}
          onChange={(e) => select(e.target.value)}
        >
          <option value="" disabled>
            No options
          </option>
          {field.presets.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
      </Control>
      <Button
        onClick={() => {
          const id = uid("preset");
          onChange({
            presets: [
              ...field.presets,
              { id, label: "New option", values: {} },
            ],
          });
          select(id);
        }}
      >
        Add preset option
      </Button>
      {record && (
        <div key={record.id}>
          <Control label="Preset option label">
            <input
              value={record.label}
              onChange={(e) =>
                modify(record.id, (r) => ({ ...r, label: e.target.value }))
              }
            />
          </Control>
          <Control label="Automatically match input variable">
            <select
              value={record.match?.field || ""}
              onChange={(e) =>
                modify(record.id, (r) => ({
                  ...r,
                  match: e.target.value
                    ? { field: e.target.value, op: "eq", value: "" }
                    : undefined,
                }))
              }
            >
              <option value="">Manual selection only</option>
              {fields
                .filter((f) => f.id !== field.id)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
            </select>
          </Control>
          {record.match && (
            <>
              <MatchOperator
                label="Preset match operator"
                value={record.match.op}
                onChange={(op) =>
                  modify(record.id, (r) => ({
                    ...r,
                    match: { ...r.match!, op },
                  }))
                }
              />
              <Control label="Preset match value">
                <input
                  value={record.match.value}
                  onChange={(e) =>
                    modify(record.id, (r) => ({
                      ...r,
                      match: { ...r.match!, value: e.target.value },
                    }))
                  }
                />
              </Control>
              <PatternHelp op={record.match.op} value={record.match.value} />
              <p className="muted">
                When no option is selected, the first matching option supplies
                values. Matching is case-sensitive; explicit target answers take
                priority.
              </p>
            </>
          )}
          <Control label="Preset target">
            <select
              value={target}
              onChange={(e) => chooseTarget(e.target.value)}
            >
              <option value="">Choose a variable</option>
              {available.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </Control>
          <Button
            disabled={!available.some((f) => f.id === target)}
            onClick={() => {
              modify(record.id, (r) => ({
                ...r,
                values: { ...r.values, [target]: "" },
              }));
              chooseTarget("");
            }}
          >
            Add preset target
          </Button>
          {Object.entries(record.values).map(([key, value]) => {
            const f = presetTarget(fields, key),
              editable = presetWritable(f);
            return (
              <div key={key}>
                <FieldInput
                  statementProject={project}
                  field={{
                    ...f,
                    id: `preset_${record.id}_${f.id}`,
                    label: `Preset value for ${f.label}`,
                    editable,
                    required: false,
                  }}
                  value={value}
                  assetUrl={services.assetUrl}
                  loadAsset={services.loadAsset}
                  assets={services.assets}
                  dam={dam}
                  upload={
                    services.upload
                      ? (file, _field, signal) =>
                          services.upload!(file, f, signal)
                      : undefined
                  }
                  onChange={(value) =>
                    modify(record.id, (r) =>
                      Object.hasOwn(r.values, key)
                        ? { ...r, values: { ...r.values, [key]: value } }
                        : r,
                    )
                  }
                />
                {!editable && (
                  <p className="muted">
                    This variable is protected and keeps its configured value.
                  </p>
                )}
                <Button
                  onClick={() =>
                    modify(record.id, (r) => ({
                      ...r,
                      values: Object.fromEntries(
                        Object.entries(r.values).filter(
                          ([name]) => name !== key,
                        ),
                      ),
                    }))
                  }
                >
                  Remove {f.label} assignment
                </Button>
              </div>
            );
          })}
          <Button
            onClick={() =>
              onChange({
                presets: field.presets.filter((r) => r.id !== record.id),
              })
            }
          >
            Remove preset option
          </Button>
        </div>
      )}
    </div>
  );
}
